'use client'

import * as PopoverPrimitive from '@radix-ui/react-popover'
import * as React from 'react'

import { cn } from '../cn'
import { Input } from './input'
import { isBodyScrollLocked } from './popover'

type Suggestion = {
  label: string
  value: string
  meta?: Record<string, string | null>
  /** Row renders dimmed and cannot be selected; keyboard navigation skips it.
   *  Use for results that are shown for context but not pickable (e.g. an
   *  entity already linked elsewhere). */
  disabled?: boolean
}

type AutocompleteInputProps = Omit<React.ComponentProps<'input'>, 'onSelect'> & {
  suggestions: Suggestion[]
  onSelect: (suggestion: Suggestion) => void
  /** Filter suggestions client-side (subsequence match, capped at 8). Default
   *  true. Set false when the host already filtered/ranked them (e.g. a
   *  server-side search) — the list renders as-is. Name mirrors Combobox. */
  shouldFilter?: boolean
  /** Show a "Searching…" row while the host fetches suggestions. */
  loading?: boolean
  /** Rendered when the query is non-empty and there are no suggestions.
   *  Without it the dropdown simply closes (legacy behavior). */
  emptyMessage?: string
  /** Show suggestions (and the loading/empty status row) even while the query
   *  is empty — e.g. pre-computed "likely matches" surfaced on focus. The
   *  dropdown also starts open (it only renders once there is content), so
   *  hosts that mount focused (autoFocus in a dialog) surface suggestions
   *  immediately — dialog focus management can swallow the initial focus
   *  event. Default false: an empty query keeps the dropdown closed (legacy
   *  behavior). */
  showOnEmpty?: boolean
  /** Replace the default two-line label/value body of each row. Highlight and
   *  disabled chrome (background, cursor, opacity) stay owned by the
   *  primitive — return only the row's inner content. */
  renderSuggestion?: (suggestion: Suggestion) => React.ReactNode
}

function fuzzyMatch(text: string, query: string): boolean {
  const lower = text.toLowerCase()
  const q = query.toLowerCase()
  let qi = 0
  for (let i = 0; i < lower.length && qi < q.length; i++) {
    if (lower[i] === q[qi]) {
      qi++
    }
  }
  return qi === q.length
}

function AutocompleteInput({
  suggestions,
  onSelect,
  className,
  value,
  onChange,
  onKeyDown,
  onFocus,
  shouldFilter = true,
  loading = false,
  emptyMessage,
  showOnEmpty = false,
  renderSuggestion,
  ...props
}: AutocompleteInputProps) {
  const [open, setOpen] = React.useState(showOnEmpty)
  const [highlightIndex, setHighlightIndex] = React.useState(-1)
  const listRef = React.useRef<HTMLUListElement>(null)

  const query = String(value ?? '')

  const filtered = React.useMemo(() => {
    if (!query) {
      // With showOnEmpty the host's suggestions render as-is on focus (capped
      // like the filtered path); otherwise an empty query shows nothing.
      return showOnEmpty ? (shouldFilter ? suggestions.slice(0, 8) : suggestions) : []
    }
    if (!shouldFilter) {
      return suggestions
    }
    return suggestions.filter((s) => fuzzyMatch(s.label, query) || fuzzyMatch(s.value, query)).slice(0, 8)
  }, [query, suggestions, shouldFilter, showOnEmpty])

  // Loading/empty rows only exist for hosts that opt in — legacy consumers
  // keep the "no matches → dropdown closes" behavior.
  const showStatusRow =
    (query.length > 0 || showOnEmpty) && filtered.length === 0 && (loading || emptyMessage !== undefined)
  const showDropdown = open && (filtered.length > 0 || showStatusRow)

  React.useEffect(() => {
    setHighlightIndex(-1)
  }, [query])

  React.useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightIndex] as HTMLElement
      el?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightIndex])

  function handleSelect(suggestion: Suggestion) {
    if (suggestion.disabled) {
      return
    }
    onSelect(suggestion)
    setOpen(false)
  }

  /** Next selectable index in `dir`, wrapping; -1 when every row is disabled. */
  function nextEnabledIndex(from: number, dir: 1 | -1): number {
    let i = from
    for (let step = 0; step < filtered.length; step++) {
      i = (i + dir + filtered.length) % filtered.length
      if (!filtered[i].disabled) {
        return i
      }
    }
    return -1
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    onKeyDown?.(e)
    if (!showDropdown) {
      return
    }

    if (e.key === 'ArrowDown' && filtered.length > 0) {
      e.preventDefault()
      setHighlightIndex((i) => nextEnabledIndex(i, 1))
    } else if (e.key === 'ArrowUp' && filtered.length > 0) {
      e.preventDefault()
      setHighlightIndex((i) => nextEnabledIndex(i <= 0 ? 0 : i, -1))
    } else if (e.key === 'Enter' && highlightIndex >= 0 && highlightIndex < filtered.length) {
      e.preventDefault()
      handleSelect(filtered[highlightIndex])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    // Popover (not raw createPortal) is required so the suggestion list works
    // when this input is hosted inside a modal Sheet/Dialog. Radix Popover
    // coordinates with Dialog's pointer-events trap; createPortal-to-body does
    // not, and clicks on the list silently pass through. See packages/ui/AGENTS.md
    // → "Floating content MUST be portaled".
    <PopoverPrimitive.Root modal={showDropdown && isBodyScrollLocked()} open={showDropdown} onOpenChange={setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <Input
          className={className}
          value={value}
          onChange={(e) => {
            onChange?.(e)
            setOpen(true)
          }}
          onFocus={(e) => {
            onFocus?.(e)
            setOpen(true)
          }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          {...props}
        />
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          style={{ width: 'var(--radix-popper-anchor-width)' }}
          className="z-[100] max-h-48 overflow-auto rounded-lg border border-border bg-popover p-1 shadow-lg">
          {showStatusRow ? (
            <p className="px-2.5 py-2.5 text-base text-muted-foreground md:py-1.5 md:text-sm">
              {loading ? 'Searching…' : emptyMessage}
            </p>
          ) : null}
          <ul ref={listRef}>
            {filtered.map((s, i) => (
              <li
                key={`${s.label}-${s.value}`}
                aria-disabled={s.disabled || undefined}
                className={cn(
                  'flex cursor-pointer flex-col rounded-md px-2.5 py-2.5 md:py-1.5 text-base md:text-sm',
                  i === highlightIndex && 'bg-accent',
                  s.disabled && 'cursor-not-allowed opacity-50',
                )}
                onMouseEnter={() => {
                  if (!s.disabled) {
                    setHighlightIndex(i)
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelect(s)
                }}>
                {renderSuggestion ? (
                  renderSuggestion(s)
                ) : (
                  <>
                    <span className="font-medium truncate">{s.label}</span>
                    {s.value && <span className="text-xs text-muted-foreground truncate">{s.value}</span>}
                  </>
                )}
              </li>
            ))}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

export { AutocompleteInput, type AutocompleteInputProps, type Suggestion }
